import { loadStripe } from '@stripe/stripe-js/pure';

/**
 * Stripe.js is not part of the page template: it cannot carry an SRI hash
 * (BRAVON-6741), so it is injected on demand by `loadStripe()` the first time
 * a payment form needs it. The `/pure` entry point is required — the default
 * one injects the script as a side effect of being imported.
 */
class StripeService {
  constructor() {
    this.instance = null;
    this.key = null;
    this.promise = null;
  }

  init(config) {
    if (config && config.key && config.key !== this.key) {
      this.key = config.key;
      this.instance = null;
      this.promise = null;
    }
  }

  /**
   * Loads the Stripe instance with the provided key.
   * @returns {Promise<Stripe|null>} A promise that resolves to the Stripe instance or null if the key is not set.
   */
  loadStripe() {
    if (!this.promise) {
      this.promise = loadStripe(this.key).then(stripe => {
        this.instance = stripe;
        return stripe;
      });
    }
    return this.promise;
  }

  updateStripeKey(config) {
    this.init(config);
  }

  handleErrors(err) {
    if (!this.instance) {
      throw 'ERROR_STRIPE_NOT_INITIALIZED';
    }
    console.log('*** handleError:().err.type: ', err ? err.type : '-');
    if (!err) {
      return null;
    }

    switch (err.type) {
      case 'card_error':
        console.log('*** handleError:().err.code: ', err.code);
        switch (err.code) {
          case 'invalid_expiry_year':
            return 'ERROR_CREDIT_CARD_EXPIRED';
          case 'incorrect_cvc':
            return 'ERROR_CARD_CVC_INCORRECT';
          case 'expired_card':
            return 'ERROR_CREDIT_CARD_EXPIRED';
          case 'processing_error':
            return 'ERROR_CARD_PROCESSING';
          case 'missing':
            return 'ERROR_MISSING_PAYMENT_CARD';
          case 'card_declined':
            console.log(
              '*** handleError:().err.decline_code: ',
              err.decline_code,
            );
            if (err.decline_code) {
              if (err.decline_code === 'insufficient_funds') {
                return 'ERROR_PAYMENT_DENIED_NO_FUNDS';
              }
            }
            return 'ERROR_PAYMENT_DENIED';
          default:
            return 'ERROR_PAYMENT_DENIED';
        }
      case 'invalid_request_error':
        console.log('*** handleError:().err.code: ', err.code);
        if (err.code === 'setup_intent_authentication_failure') {
          return 'ERROR_PAYMENT_METHOD_AUTHENTICATE_FAILED';
        }
        return 'ERROR_CARD_PROCESSING';
      default:
        switch (err.message) {
          case 'Invalid positive integer':
            return 'ERROR_INVALID_PRICE';
          case 'You cannot use a live bank account number when making transfers or debits in test mode':
            return 'ERROR_INVALID_IBAN_TEST';
          case "We couldn't find the bank for this account number":
            return 'ERROR_INVALID_IBAN';
          case 'Date of birth must be in the past':
            return 'ERROR_INVALID_BIRTHDAY';
          default:
            if (err.message.indexOf('Invalid integer:') !== -1) {
              return 'ERROR_INVALID_PRICE';
            }
            if (err.message.indexOf('Invalid currency:') !== -1) {
              return 'ERROR_INVALID_CURRENCY';
            }
            if (err.message.indexOf(' postal code') !== -1) {
              return 'ERROR_INVALID_POST_CODE_COUNTRY';
            }
            return err.message;
        }
    }
  }

  async createToken(element) {
    if (!this.instance) {
      throw 'ERROR_STRIPE_NOT_INITIALIZED';
    }

    const { error, token } = await this.instance.createToken(element);
    if (error) {
      throw this.handleError(error);
    }
    return {
      ...(token.card || {}),
      token: token.id,
    };
  }

  elements() {
    if (!this.instance) {
      throw 'ERROR_STRIPE_NOT_INITIALIZED';
    }

    return this.instance.elements();
  }
}

const instance = new StripeService();
export default instance;
